import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';

interface LegalSection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
  contactLink?: boolean;
}

@Component({
  selector: 'app-refund-policy',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './refund-policy.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../terms/terms.scss']
})
export class RefundPolicy {
  readonly lastUpdated = '20 September 2026';

  readonly sections: LegalSection[] = [
    {
      id: 'overview',
      title: '1. Overview',
      paragraphs: [
        'This page explains how refunds and cancellations work for the paid Services on everyrupeespent.com. It is a plain-language summary of Section 9-11 of our Terms of Service; where the two differ, the Terms of Service govern.',
        'All payments are processed in Indian Rupees (₹), securely through Razorpay. We never see or store your card, UPI or bank credentials.'
      ]
    },
    {
      id: 'one-time',
      title: '2. One-time payments — Rental Agreement Generator',
      paragraphs: [
        'This is a single, non-recurring payment — there is no subscription or auto-renewal.',
        'Because work begins as soon as payment is confirmed (an agreement draft is generated), this payment is non-refundable once processing has started.',
        'If a technical issue on our end prevents us from delivering the agreement document at all, we will issue a full refund.'
      ]
    },
    {
      id: 'subscriptions',
      title: '3. Annual plans — Reminders, Budget & Expense Tracker, Rental Management System',
      paragraphs: [
        'Premium plans for Reminders, the Budget & Expense Tracker and the Rental Management System are paid one year at a time. This is a single payment for 365 days of access — it does not auto-renew, and your card or UPI is never charged again automatically.',
        'Your plan simply expires at the end of the period you paid for. To keep premium access, make a new payment any time before or after it expires. Paying again before your current period ends extends your access by 365 days from your existing expiry date (not from the new payment date), so renewing early never costs you time you already paid for.'
      ]
    },
    {
      id: 'how-to-cancel',
      title: '4. Ending a plan',
      paragraphs: [
        'Because these are one-time annual payments rather than auto-renewing subscriptions, there is no recurring charge to cancel and no cancellation request to make. If you simply choose not to make a new payment when your plan expires, your account reverts to the free tier automatically — no email or support request is needed.'
      ]
    },
    {
      id: 'plan-changes',
      title: '5. Switching plans mid-term',
      paragraphs: [
        'When your paid period simply expires without renewal, you move to the free tier automatically and nothing is archived — existing items you already created remain visible, though free-tier limits apply to anything new you try to add from that point.',
        'For Reminders and the Rental Management System, if you buy a different paid tier before your current one expires and you are holding more items than the new tier allows, you will be asked to archive the excess before the switch takes effect. This applies only to switching between paid tiers mid-term, not to a plan lapsing to free at the end of its period. This option is not currently available for the Budget & Expense Tracker, which has a single premium tier. See Section 11 of our Terms of Service for the full archiving rules.'
      ]
    },
    {
      id: 'charged-in-error',
      title: '6. Charged in error, or a payment that failed to process',
      paragraphs: [
        'If you believe you have been charged in error, charged twice, or paid but did not receive the Service (for example, a payment was deducted but no agreement draft was generated), contact us with your payment reference. We investigate every report and will refund confirmed errors.'
      ],
      contactLink: true
    },
    {
      id: 'timelines',
      title: '7. Refund timelines',
      paragraphs: [
        'Approved refunds are issued to your original payment method via Razorpay. Once we approve a refund, Razorpay and your bank typically take 5-10 business days to reflect it in your account; this timeline is set by Razorpay and your bank, not by us.'
      ]
    },
    {
      id: 'contact',
      title: '8. Questions',
      paragraphs: [
        'For any question about a payment, refund or cancellation, write to us and we will respond promptly.'
      ],
      contactLink: true
    }
  ];
}
